import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CButton, CRow, CCol, CAlert, CSpinner,
  CFormLabel, CFormTextarea, CFormSelect, CTable, CTableHead, CTableRow,
  CTableHeaderCell, CTableBody, CTableDataCell,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
  CBadge,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilReload, cilSend, cilCheck, cilNotes, cilHistory, cilExternalLink } from '@coreui/icons';
import api from '../../services/api';
import { getToken } from '../../services/auth';
import { buildWhatsAppSendUrl } from '../../utils/waMeUrl';

/** Digits only, MY-style 0… → 60… for WhatsApp `phone=` */
function phoneForWhatsApp(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('60')) return digits;
  if (digits.startsWith('0')) return '60' + digits.slice(1);
  return digits;
}

function fillTemplate(body, customer) {
  return String(body || '')
    .replace(/\{\{nama\}\}/g, customer.nama || '')
    .replace(/\{\{telhp\}\}/g, customer.telhp || '');
}

function emptyQueueExplanation(s) {
  if (!s) return 'No customers in the queue right now.';
  const {
    eligiblePool = 0, pickableNow = 0, heldByOthers = 0, unsubscribed = 0, inCooldown = 0,
  } = s;
  if (eligiblePool === 0) {
    return `No eligible customers: ${inCooldown} in 3‑month cooldown after a send, ${unsubscribed} unsubscribed from marketing.`;
  }
  if (pickableNow === 0 && heldByOthers > 0) {
    return `All ${eligiblePool} eligible customer(s) are temporarily in another staff member’s batch (${heldByOthers} locked). Wait until those batches expire (about 10 minutes) or refresh later.`;
  }
  return `Queue is empty but ${eligiblePool} customer(s) match the pool (${pickableNow} available to claim). Try Refresh. Unsubscribed: ${unsubscribed}, in cooldown: ${inCooldown}.`;
}

const STATUS_COLORS = {
  'Baru': 'primary',
  'Dalam Proses': 'warning',
  'Selesai': 'success',
  'Dipulangkan': 'secondary',
  'Menunggu Maklumbalas': 'info',
  'Waiting to Pickup': 'info',
};

function JobsModal({ customer, onClose }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!customer) return;
    setLoading(true);
    setError('');
    api.get(`/jobs/by-customer/${encodeURIComponent(customer.uid)}`)
      .then((r) => setJobs(r.data.jobs || []))
      .catch((e) => setError(e.response?.data?.message || 'Could not load jobs.'))
      .finally(() => setLoading(false));
  }, [customer]);

  return (
    <CModal visible={!!customer} onClose={onClose} size="lg" scrollable>
      <CModalHeader>
        <CModalTitle>
          Previous Jobs — {customer?.nama || customer?.uid}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {loading && <div className="text-center py-4"><CSpinner color="success" /></div>}
        {error && <CAlert color="danger">{error}</CAlert>}
        {!loading && !error && jobs.length === 0 && (
          <p className="text-muted">No previous jobs found for this customer.</p>
        )}
        {!loading && jobs.length > 0 && (
          <CAccordion alwaysOpen={false}>
            {jobs.map((job, idx) => {
              const devices = Array.isArray(job.alat) && job.alat.length > 0
                ? job.alat.join(', ')
                : job.peralatan_lain || '—';
              const statusColor = STATUS_COLORS[job.status] || 'secondary';
              return (
                <CAccordionItem key={job.jobcode} itemKey={idx}>
                  <CAccordionHeader>
                    <div className="d-flex align-items-center gap-2 w-100">
                      <span className="fw-semibold me-2">{job.jobcode}</span>
                      <CBadge color={statusColor} className="me-2">{job.status || '—'}</CBadge>
                      <span className="text-muted small">{devices}</span>
                      <span className="ms-auto text-muted small me-3">{job.date || ''}</span>
                    </div>
                  </CAccordionHeader>
                  <CAccordionBody>
                    <CRow className="g-2 mb-2">
                      <CCol sm={6}>
                        <div className="small text-muted">Type</div>
                        <div>{job.jenis || '—'}</div>
                      </CCol>
                      <CCol sm={6}>
                        <div className="small text-muted">Priority</div>
                        <div>{job.keutamaan || '—'}</div>
                      </CCol>
                      <CCol sm={6}>
                        <div className="small text-muted">Cost</div>
                        <div>{job.kos || '—'}</div>
                      </CCol>
                      <CCol sm={6}>
                        <div className="small text-muted">Admin</div>
                        <div>{job.admin_id || '—'}</div>
                      </CCol>
                      {job.catatan && (
                        <CCol xs={12}>
                          <div className="small text-muted">Notes</div>
                          <div className="small">{job.catatan}</div>
                        </CCol>
                      )}
                    </CRow>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const token = getToken();
                        if (token) localStorage.setItem('token', token);
                        window.open(
                          `${process.env.PUBLIC_URL || ''}/jobs/${encodeURIComponent(job.jobcode)}/edit`,
                          '_blank',
                          'noopener,noreferrer',
                        );
                      }}
                    >
                      <CIcon icon={cilExternalLink} className="me-1" /> View Job
                    </button>
                  </CAccordionBody>
                </CAccordionItem>
              );
            })}
          </CAccordion>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>Close</CButton>
      </CModalFooter>
    </CModal>
  );
}

export default function MarketingSendPage() {
  const [queue, setQueue] = useState([]);
  const [queueSummary, setQueueSummary] = useState(null);
  const [leaseExpiresAt, setLeaseExpiresAt] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUid, setBusyUid] = useState(null);
  const [jobsCustomer, setJobsCustomer] = useState(null);

  const loadTemplates = useCallback(() => {
    api.get('/marketing/templates')
      .then((r) => {
        setTemplates(r.data.templates || []);
      })
      .catch(() => {});
  }, []);

  const loadQueue = useCallback(() => {
    setError('');
    api.get('/marketing/queue')
      .then((r) => {
        setQueue(r.data.queue || []);
        setLeaseExpiresAt(r.data.leaseExpiresAt || null);
        setQueueSummary(r.data.summary || null);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Could not load marketing queue.');
        setQueue([]);
        setQueueSummary(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    loadTemplates();
    loadQueue();
  }, [loadQueue, loadTemplates]);

  useEffect(() => {
    if (!templates.length || templateId) return;
    const first = templates[0];
    setTemplateId(String(first.id));
    setMessageBody(first.body || '');
  }, [templates, templateId]);

  function onPickTemplate(id) {
    setTemplateId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    if (t) setMessageBody(t.body || '');
  }

  function waUrl(customer) {
    const phone = phoneForWhatsApp(customer.telhp);
    if (!phone) return null;
    const text = fillTemplate(messageBody, customer);
    return buildWhatsAppSendUrl(phone, text);
  }

  async function markSent(customer) {
    const text = fillTemplate(messageBody, customer);
    setBusyUid(customer.uid);
    setError('');
    try {
      const payload = {
        uid: customer.uid,
        messagePreview: text.slice(0, 600),
      };
      if (templateId) payload.templateId = parseInt(templateId, 10);
      await api.post('/marketing/mark-sent', payload);
      loadQueue();
    } catch (err) {
      setError(err.response?.data?.message || 'Mark sent failed.');
    } finally {
      setBusyUid(null);
    }
  }

  let leaseHint = '';
  if (leaseExpiresAt) {
    const t = new Date(leaseExpiresAt);
    if (!Number.isNaN(t.getTime())) {
      leaseHint = `This batch refreshes after ${t.toLocaleString()} (about ${Math.max(0, Math.ceil((t - Date.now()) / 60000))} min).`;
    }
  }

  return (
    <>
      <JobsModal customer={jobsCustomer} onClose={() => setJobsCustomer(null)} />
      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Marketing — Send WhatsApp</h5>
          <div className="text-muted small mt-1">
            Up to <strong>5 customers per logged-in staff</strong> (oldest registrations first). Customers currently held in another staff member&apos;s batch are skipped so you get different names.
            Open WhatsApp from your own account, then click <strong>Mark sent</strong>. The same customer can reappear after 3 months. Templates:{' '}
            <Link to="/marketing/templates">message templates</Link> (<code>{'{{nama}}'}</code>, <code>{'{{telhp}}'}</code>).
          </div>
        </CCol>
        <CCol xs="auto">
          <CButton color="secondary" variant="outline" size="sm" onClick={() => { setLoading(true); loadQueue(); }}>
            <CIcon icon={cilReload} className="me-1" /> Refresh queue
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {leaseHint && <CAlert color="info" className="py-2">{leaseHint}</CAlert>}

      <CCard className="mb-3 border-0 shadow-sm">
        <CCardHeader className="bg-white d-flex align-items-center gap-2">
          <CIcon icon={cilNotes} className="text-success" />
          Message
        </CCardHeader>
        <CCardBody>
          <CRow className="g-3">
            <CCol md={4}>
              <CFormLabel>Template</CFormLabel>
              <CFormSelect
                value={templateId}
                onChange={(e) => onPickTemplate(e.target.value)}
                disabled={!templates.length}
              >
                {!templates.length && <option value="">No templates — type below or add templates</option>}
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={8}>
              <CFormLabel>Text (opens in your WhatsApp)</CFormLabel>
              <CFormTextarea rows={4} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Hi {{nama}}, …" />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CCard className="border-0 shadow-sm">
        <CCardHeader className="bg-white">Queue (max 5)</CCardHeader>
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="success" /></div>
          ) : queue.length === 0 ? (
            <div className="p-4 text-muted">
              <p className="mb-2">{emptyQueueExplanation(queueSummary)}</p>
              {queueSummary && (
                <ul className="small mb-0 ps-3">
                  <li>Eligible pool (not unsub, not in 3‑mo cooldown): <strong>{queueSummary.eligiblePool}</strong></li>
                  <li>You can claim now (not in someone else&apos;s active batch): <strong>{queueSummary.pickableNow}</strong></li>
                  <li>Held in another staff batch: <strong>{queueSummary.heldByOthers}</strong></li>
                </ul>
              )}
            </div>
          ) : (
            <CTable responsive hover className="mb-0 align-middle">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Name</CTableHeaderCell>
                  <CTableHeaderCell>Mobile</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {queue.map((c) => {
                  const url = waUrl(c);
                  return (
                    <CTableRow key={c.uid}>
                      <CTableDataCell>
                        <Link to={`/customers/${c.uid}/edit`}>{c.nama || c.uid}</Link>
                        <div className="small text-muted">{c.uid}</div>
                      </CTableDataCell>
                      <CTableDataCell>{c.telhp || '—'}</CTableDataCell>
                      <CTableDataCell className="text-end text-nowrap">
                        <CButton
                          size="sm"
                          color="secondary"
                          variant="outline"
                          className="me-1"
                          onClick={() => setJobsCustomer(c)}
                          title="Previous jobs"
                        >
                          <CIcon icon={cilHistory} className="me-1" /> Jobs
                        </CButton>
                        {url ? (
                          <CButton
                            component="a"
                            size="sm"
                            variant="outline"
                            className="me-1"
                            style={{ borderColor: '#25d366', color: '#25d366' }}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <CIcon icon={cilSend} className="me-1" /> WhatsApp
                          </CButton>
                        ) : (
                          <span className="text-muted small me-2">No mobile</span>
                        )}
                        <CButton
                          size="sm"
                          color="success"
                          variant="outline"
                          disabled={busyUid === c.uid}
                          onClick={() => markSent(c)}
                        >
                          {busyUid === c.uid ? <CSpinner size="sm" /> : <><CIcon icon={cilCheck} className="me-1" /> Mark sent</>}
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
